import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Blog.module.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const API_URL = 'https://yeskondomedia.com/ymplblogsadmin/api.php';

const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const readTime = (text = '') => {
  const words = text.replace(/<[^>]*>/g, '').split(/\s+/).length;
  return Math.max(1, Math.ceil(words / 200));
};

/* ─ Skeleton Card ─ */
const SkeletonCard = () => (
  <div className={styles.skeleton_card}>
    <div className={`${styles.skel} ${styles.skel_img}`}></div>
    <div className={styles.skel_body}>
      <div className={`${styles.skel} ${styles.skel_line} ${styles.skel_short}`}></div>
      <div className={`${styles.skel} ${styles.skel_title}`}></div>
      <div className={`${styles.skel} ${styles.skel_line}`}></div>
      <div className={`${styles.skel} ${styles.skel_line} ${styles.skel_short}`}></div>
    </div>
  </div>
);

/* ─ Blog Card ─ */
const BlogCard = ({ blog }) => {
  let imageUrl = blog.image_url;
  if (imageUrl && !imageUrl.startsWith('http')) {
    imageUrl = `https://yeskondomedia.com/ymplblogsadmin/image.php?path=${imageUrl}`;
  }
  const fallback = 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?auto=format&fit=crop&w=800&q=80';

  return (
    <article className={styles.blog_card}>
      <Link to={`/blog/${blog.slug}`} className={styles.card_img_wrap}>
        <img
          src={imageUrl || fallback}
          alt={blog.title}
          className={styles.card_img}
          loading="lazy"
          onError={(e) => { e.target.src = fallback; }}
        />
        <span className={styles.card_category}>{blog.category || 'Marketing'}</span>
      </Link>

      <div className={styles.card_body}>
        <div className={styles.card_meta}>
          <span>{formatDate(blog.created_at)}</span>
          <span className={styles.meta_dot}></span>
          <span>{readTime(blog.content)} min read</span>
          <span className={styles.meta_dot}></span>
          <span>Yeskondo</span>
        </div>
        <Link to={`/blog/${blog.slug}`} style={{ textDecoration: 'none' }}>
          <h2 className={styles.card_title}>{blog.title}</h2>
        </Link>
        <p className={styles.card_desc}>
          {blog.seo_description || 'Click to read the full article and explore expert insights from our team.'}
        </p>
        <Link to={`/blog/${blog.slug}`} className={styles.card_read_more}>
          Read Article
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </article>
  );
};

/* ─ Sidebar ─ */
const Sidebar = ({ blogs }) => {
  const recent = blogs.slice(0, 4);
  const categories = [...new Set(blogs.map(b => b.category || 'Marketing'))];

  return (
    <aside className={styles.sidebar}>

      {/* About Widget */}
      <div className={styles.sidebar_widget}>
        <h3 className={styles.widget_title}>About Yeskondo</h3>
        <p className={styles.about_widget_text}>
          Yeskondo Media Pvt. Ltd. is Pune's premier 360° marketing agency.
          Our blog shares expert insights on digital marketing, OOH, branding, and business growth.
        </p>
        <Link to="/contact" className={styles.widget_cta}>
          Work With Us →
        </Link>
      </div>

      {/* Recent Posts */}
      {recent.length > 0 && (
        <div className={styles.sidebar_widget}>
          <h3 className={styles.widget_title}>Recent Posts</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {recent.map((b, i) => {
              let img = b.image_url;
              if (img && !img.startsWith('http')) {
                img = `https://yeskondomedia.com/ymplblogsadmin/image.php?path=${img}`;
              }
              return (
                <Link
                  key={i}
                  to={`/blog/${b.slug}`}
                  style={{ display: 'flex', gap: '12px', textDecoration: 'none', alignItems: 'flex-start' }}
                >
                  <img
                    src={img || 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?auto=format&fit=crop&w=120&q=60'}
                    alt={b.title}
                    style={{ width: '58px', height: '48px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                    loading="lazy"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?auto=format&fit=crop&w=120&q=60'; }}
                  />
                  <div>
                    <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-color)', lineHeight: 1.35, marginBottom: '4px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {b.title}
                    </p>
                    <p style={{ fontSize: '0.72rem', color: '#aaa' }}>{formatDate(b.created_at)}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div className={styles.sidebar_widget}>
          <h3 className={styles.widget_title}>Categories</h3>
          <ul className={styles.category_list}>
            {categories.map((cat, i) => (
              <li key={i} className={styles.category_item}>
                <span>{cat}</span>
                <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                  {blogs.filter(b => (b.category || 'Marketing') === cat).length}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contact Widget */}
      <div className={styles.sidebar_widget}>
        <h3 className={styles.widget_title}>Get in Touch</h3>
        <div className={styles.contact_widget_info}>
          <div className={styles.contact_widget_row}>
            <span className={styles.contact_widget_icon}>📍</span>
            <span>Deccan, Pune — Maharashtra, India</span>
          </div>
          <div className={styles.contact_widget_row}>
            <span className={styles.contact_widget_icon}>📞</span>
            <a href="tel:+919922966739" style={{ color: 'inherit', textDecoration: 'none' }}>+91 99229 66739</a>
          </div>
          <div className={styles.contact_widget_row}>
            <span className={styles.contact_widget_icon}>✉️</span>
            <a href="mailto:info@yeskondomedia.com" style={{ color: 'inherit', textDecoration: 'none' }}>info@yeskondomedia.com</a>
          </div>
        </div>
        <Link to="/contact" className={styles.widget_cta}>
          Free Consultation →
        </Link>
      </div>

    </aside>
  );
};

/* ─ Main Blog Component ─ */
const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}?action=get_blogs`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') setBlogs(data.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Marketing Insights & Expert Blog | Yeskondo Media Pune</title>
        <meta name="description" content="Explore expert articles on digital marketing, OOH advertising, brand strategy and business growth from Pune's best marketing agency — Yeskondo Media." />
        <link rel="canonical" href="https://www.yeskondomedia.com/blog" />
      </Helmet>

      <main className={styles.blog_page}>

        {/* ── Hero ── */}
        <div className={styles.page_hero}>
          <div className={styles.hero_inner}>
            <span className={styles.hero_eyebrow}>Expert Insights</span>
            <h1 className={styles.hero_title}>
              Marketing Tips &amp; <span>Industry Insights</span>
            </h1>
            <p className={styles.hero_desc}>
              Stay ahead with expert perspectives on digital marketing, brand strategy,
              OOH advertising and business growth — straight from Pune's best marketing team.
            </p>
          </div>
        </div>

        {/* ── Layout: Grid + Sidebar ── */}
        <div className={styles.blog_layout}>

          {/* Blog Grid */}
          <div className={styles.blog_grid}>
            {loading ? (
              [1, 2, 3, 4].map(n => <SkeletonCard key={n} />)
            ) : blogs.length === 0 ? (
              <div className={styles.no_blogs}>No articles published yet. Check back soon!</div>
            ) : (
              blogs.map(blog => <BlogCard key={blog.id} blog={blog} />)
            )}
          </div>

          {/* Sidebar */}
          <Sidebar blogs={blogs} />

        </div>
      </main>
    </>
  );
};

export default Blog;
